/**
 * ISO-3166 alpha-2 codes for countries where a cookie consent banner is
 * legally required before non-essential analytics/marketing cookies may fire.
 *
 * Covers the EU/EEA (27 EU member states + Iceland, Liechtenstein, Norway)
 * plus the United Kingdom (post-Brexit PECR) and Switzerland (revFADP).
 */
export const CONSENT_REQUIRED_COUNTRIES: readonly string[] = Object.freeze([
  // EU
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  // EEA non-EU
  "IS",
  "LI",
  "NO",
  // UK + CH
  "GB",
  "CH",
]);

/**
 * Returns true if a visitor from the given country must see the cookie banner
 * before analytics/marketing cookies are set. Treats `null` as "consent
 * required" — that's the safe default when geo lookup fails.
 */
export const isConsentRequiredCountry = (country: string | null): boolean => {
  if (!country) return true;
  return CONSENT_REQUIRED_COUNTRIES.includes(country.toUpperCase());
};

const COUNTRY_CACHE_KEY = "user_country";
const COUNTRY_CACHE_AT_KEY = "user_country_at";
const COUNTRY_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

/**
 * Resolves the visitor's ISO-3166 alpha-2 country code via ipinfo.io.
 *
 * - The ipinfo token is passed in by the caller, since this shared util has no
 *   access to an app-specific config object.
 * - Cached in localStorage for 24h to avoid hammering ipinfo.
 * - Returns null on fetch failure, missing token, or SSR — callers should
 *   treat null as "consent required" (safe default).
 */
export const getCountry = async (
  token: string | undefined,
): Promise<string | null> => {
  if (typeof window === "undefined") return null;

  try {
    const cached = window.localStorage.getItem(COUNTRY_CACHE_KEY);
    const cachedAt = window.localStorage.getItem(COUNTRY_CACHE_AT_KEY);
    if (
      cached &&
      cachedAt &&
      Date.now() - Number(cachedAt) < COUNTRY_CACHE_TTL_MS
    ) {
      // Verbose-only — silent under DevTools' default filter. Surfaces the
      // short-circuit so QA can tell "fetch never fired" from "fetch failed."
      console.debug("Geo: localStorage cache hit", {
        country: cached,
        age_ms: Date.now() - Number(cachedAt),
      });
      return cached;
    }
  } catch {
    // localStorage can throw in private browsing or with quota issues — fall
    // through to the network fetch.
  }

  if (!token) {
    console.warn("Geo detection skipped: VITE_PUBLIC_IPINFO_TOKEN not set");
    return null;
  }
  // Defensive guard: if dotenvx failed to decrypt at build time, the env var
  // ends up holding the still-encrypted blob (prefixed `encrypted:`). Calling
  // ipinfo with that would 403 every time — bail early so we don't pollute
  // logs and so we fall back to showing the banner.
  if (token.startsWith("encrypted:")) {
    console.warn(
      "Geo detection skipped: VITE_PUBLIC_IPINFO_TOKEN looks encrypted — " +
        "dotenvx likely failed to decrypt at build time. Check the deploy " +
        "environment's DOTENV_PRIVATE_KEY_PRODUCTION secret.",
    );
    return null;
  }

  try {
    // ipinfo migrated to the Lite API: new host (api.ipinfo.io), new endpoint
    // (/lite/me returns geo for the caller's IP), and Bearer-header auth
    // instead of the legacy `?token=` query string. The Lite response shape
    // also changed — the ISO-3166 alpha-2 code now lives in `country_code`;
    // `country` is the full country name (e.g. "Estonia"). Comparing the full
    // name against our ISO-code list would silently fail and treat every
    // visitor as non-EEA, so this parses `country_code` specifically.
    const res = await fetch("https://api.ipinfo.io/lite/me", {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      console.warn("Geo detection failed: HTTP", res.status);
      return null;
    }
    const data: { country_code?: string } = await res.json();
    if (data.country_code) {
      try {
        window.localStorage.setItem(COUNTRY_CACHE_KEY, data.country_code);
        window.localStorage.setItem(COUNTRY_CACHE_AT_KEY, String(Date.now()));
      } catch {
        // Cache write failed; still return the value.
      }
      return data.country_code;
    }
    return null;
  } catch (e) {
    console.warn("Geo detection failed", e);
    return null;
  }
};
